import log from 'loglevel'
import { floor, isNil, template, toNumber } from 'lodash/fp'
import { fetchUtils, type RaRecord } from 'react-admin'
import axios from 'axios'

import {
  CognitoUserPool,
  type CognitoUserSession
} from 'amazon-cognito-identity-js'

import cognito from '../../../../cdk/cognitoApi/lib/lambda-api/src/clients/userPools.json'
import {
  type ElectricityDemandData,
  type AnnounceItem,
  type ElectricityChartDailyKind,
  type ElectricityChartMonthlyKind,
  type ElectricityChartYearlyKind
} from '../../../../cdk/bapp/lib/lambda-api/src/types'
import { assign } from 'lodash'
import { type PartnerUser } from '@mzc-watchover/adminapp-appsync'
import { type Comment } from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'

// import config from '../../../../cdk/solarApp/assets/exp/014ceb4b-603f-469a-8605-076478b4d331/config.json'
// import configChart from '../../../../cdk/solarApp/assets/exp/014ceb4b-603f-469a-8605-076478b4d331/configChart.json'
// import config from '../../../../cdk/solarApp/assets/stg/fdeb2ae0-47d1-4d41-bd0c-7ffdab7f4598/config.json'
// import configChart from '../../../../cdk/solarApp/assets/stg/fdeb2ae0-47d1-4d41-bd0c-7ffdab7f4598/configChart.json'
// import config from '../../../../cdk/solarApp/assets/prd/e1a275a2-83c6-415f-b077-2f8ac1e00aa3/config.json'
// import configChart from '../../../../cdk/solarApp/assets/prd/e1a275a2-83c6-415f-b077-2f8ac1e00aa3/configChart.json'
// import config from '../../../../cdk/solarApp/assets/prd/fd4b63fd-5ae9-4003-b9e3-3a9605125876/config.json'
// import configChart from '../../../../cdk/solarApp/assets/prd/fd4b63fd-5ae9-4003-b9e3-3a9605125876/configChart.json'

const env = process.env['ENV'] as string
const userPools: Record<string, { userPoolId: string; clientId: string }> =
  cognito[env as keyof typeof cognito]
const holidaysUrl = 'https://holidays-jp.github.io/api/v1/<%= year %>/date.json'
export const url = !isNil(process.env['API_LOCAL'])
  ? 'http://localhost:14001/v1'
  : {
      prd: 'https://api-v2.bapp.terashite.jp/v1',
      stg: 'https://api-v2-stg.bapp.terashite.jp/v1',
      int: 'https://api-v2-int.bapp.dev.terashite.jp/v1',
      dev: 'https://api-v2-dev.bapp.terashite.jp/v1',
      exp: 'https://api-v2-exp.bapp.dev.terashite.jp/v1'
    }[env]
const configUrl = `${url as string}/config`
const announceUrl = `${url as string}/announce/sapp/all`
const getPartnerIdUrl = `${url as string}/code`
const WAIT_LIMIT = toNumber(process.env['WAIT_LIMIT'] ?? 300000)

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const getCustomHeaders = (
  options: fetchUtils.Options = {},
  partner: string | undefined
): Headers | undefined => {
  if (isNil(partner) || isNil(userPools[partner])) return undefined
  const { userPoolId: UserPoolId, clientId: ClientId } = userPools[partner]
  const userPool = new CognitoUserPool({
    UserPoolId,
    ClientId
  })
  const cognitoUser = userPool.getCurrentUser()
  if (isNil(cognitoUser)) return undefined
  let session: any
  cognitoUser.getSession(
    (error: Error, newSession: CognitoUserSession | null) => {
      if (!isNil(error)) {
        log.error('[DataProvider.fetchJson]', { error })
        return
      }
      session = newSession
    }
  )
  if (isNil(session)) return undefined
  const customHeaders = (
    !isNil(options.headers)
      ? options.headers
      : new Headers({
          Accept: 'application/json'
        })
  ) as Headers
  customHeaders.set('Authorization', session.getIdToken().getJwtToken())
  if (!isNil(partner)) {
    customHeaders.set('X-Partner', partner)
  }
  return customHeaders
}
export const fetchJson =
  (partner: string | undefined) =>
  (url: string, options: fetchUtils.Options = {}): any => {
    const customHeaders = getCustomHeaders(options, partner)
    if (isNil(customHeaders)) return undefined
    options.headers = customHeaders
    return fetchUtils.fetchJson(url, options)
  }

export const getElectricity = async <T>(
  partner: string | undefined,
  kind: string,
  date: string,
  compareTo?:
    | ElectricityChartDailyKind
    | ElectricityChartMonthlyKind
    | ElectricityChartYearlyKind
): Promise<T | undefined> => {
  const { status, json } = await fetchJson(partner)(
    `${url as string}/electricity/${kind}/all/${date}/${
      compareTo as string
    }/false`,
    {
      method: 'GET'
    }
  )
  if (status !== 200) return undefined
  const { url: resultUrl } = json
  const response = await fetch(resultUrl)
  if (response.status !== 200) return undefined
  return await response.json()
}
export const getElectricityBySpin = async <T>(
  partner: string | undefined,
  spin: string,
  kind: string,
  date: string,
  compareTo?:
    | ElectricityChartDailyKind
    | ElectricityChartMonthlyKind
    | ElectricityChartYearlyKind,
  demand?: boolean
): Promise<T | undefined> => {
  const { status, json } = await fetchJson(partner)(
    !isNil(compareTo)
      ? `${url as string}/electricity/${kind}/compare/${spin}/${date}/${
          compareTo as string
        }`
      : `${url as string}/electricity/${kind}/data/${spin}/${date}/${
          isNil(demand) || !demand ? 'false' : 'true'
        }`,
    {
      method: 'GET'
    }
  )
  return status !== 200 ? undefined : json
}
export const getElectricityTargets = async <T>(
  partner: string | undefined,
  kind: string,
  date: string,
  targetKeys?: string[],
  targetValues?: string[]
): Promise<T | undefined> => {
  const { status, json } = await fetchJson(partner)(
    `${url as string}/electricity/${kind}/${date}`,
    {
      method: 'POST',
      body: JSON.stringify({
        targetKeys: targetKeys ?? [],
        targetValues: targetValues ?? []
      })
    }
  )
  return status !== 200 ? undefined : json
}
export const getHolidays = async (
  year: number
): Promise<Record<string, string> | undefined> => {
  const response = await fetch(template(holidaysUrl)({ year }))
  if (response.status !== 200) return undefined
  return await response.json()
}
export const getConfig = async (
  partner: string
): Promise<Record<string, any> | undefined> => {
  if (isNil(process.env['CONFIG_LOCAL'])) {
    const { status, json } = await fetchJson(partner)(configUrl, {
      method: 'GET'
    })
    return status !== 200 ? undefined : json
  } else {
    const config = await import(
      `../../../../cdk/solarApp-v2/assets/${env}/${partner}/config.json`
    )
    const configChart = await import(
      `../../../../cdk/solarApp-v2/assets/${env}/${partner}/configChart.json`
    )
    return { ...config, Chart: configChart }
  }
}
const getData = async <T>(
  partner: string,
  params: T,
  path: string
): Promise<any | undefined> => {
  if (isNil(url)) return undefined
  const customHeaders = getCustomHeaders({}, partner)
  if (isNil(customHeaders)) return undefined
  const headers = {}
  customHeaders.forEach((value: string, key: string, _parent: Headers) => {
    assign(headers, { [key]: value })
  })
  const startTime = performance.now()
  const { status, data: responseData } = await axios
    .post(`${url}/${path}`, params, {
      headers
    })
    .catch((error) => {
      log.error('[functions.getData]', { path, error })
      return { data: undefined, status: 500 }
    })
  if (isNil(responseData)) return undefined
  const { session: startSession } = responseData as { session: string }
  if (status !== 200) return undefined
  let waitDuration = 1000
  let count = 0
  while (true) {
    const { data } = await axios
      .get(`${url}/session/${startSession}`, {
        headers
      })
      .catch((error) => {
        log.error('[funtions.getData]', { path, error })
        return { data: undefined }
      })
    if (isNil(data)) break
    const { session: sessionValue } = data
    if (sessionValue === 'done') {
      const { status, data: urlData } = await axios
        .get(`${url}/${path}/${startSession}`, {
          headers
        })
        .catch((error) => {
          log.error('[functions.getData]', { path, error })
          const { status } = error
          return { data: undefined, status }
        })
      if (status !== 200) return { data: undefined }
      const { url: resultUrl } = urlData
      const response = await fetch(resultUrl)
      if (response.status !== 200) return undefined
      const elapsed = performance.now()
      log.debug(
        `[functions.getData] execution time: ${
          floor((elapsed - startTime) / 100) / 10
        }`
      )
      return await response.json()
    }
    if (count === 10) waitDuration = 10000
    if (count === 5) waitDuration = 5000
    await new Promise((resolve) => setTimeout(resolve, waitDuration))
    if (++count === WAIT_LIMIT / waitDuration) break
  }
}
export const getSpotData = async (
  partner: string,
  data: {
    base: string
    targets: Array<{
      user: Partial<PartnerUser>
      daily: {
        date: string
        compareTo: ElectricityChartDailyKind
        threshold: number
      }
      monthly: {
        date: string
        compareTo: ElectricityChartMonthlyKind
        threshold: number
      }
      yearly: { date: string; compareTo: ElectricityChartYearlyKind }
    }>
  }
): Promise<any | undefined> =>
  await getData<{
    base: string
    targets: Array<{
      user: Partial<PartnerUser>
      daily: {
        date: string
        compareTo: ElectricityChartDailyKind
        threshold: number
      }
      monthly: {
        date: string
        compareTo: ElectricityChartMonthlyKind
        threshold: number
      }
      yearly: { date: string; compareTo: ElectricityChartYearlyKind }
    }>
  }>(partner, data, 'electricity/spot')
export const getSpotPdf = async (
  partner: string,
  data: {
    base: string
    targets: Array<{
      user: Partial<PartnerUser>
      daily: {
        date: string
        compareTo: ElectricityChartDailyKind
        threshold: number
      }
      monthly: {
        date: string
        compareTo: ElectricityChartMonthlyKind
        threshold: number
      }
      yearly: { date: string; compareTo: ElectricityChartYearlyKind }
    }>
  }
): Promise<any | undefined> =>
  await getData<{
    base: string
    targets: Array<{
      user: Partial<PartnerUser>
      daily: {
        date: string
        compareTo: ElectricityChartDailyKind
        threshold: number
      }
      monthly: {
        date: string
        compareTo: ElectricityChartMonthlyKind
        threshold: number
      }
      yearly: { date: string; compareTo: ElectricityChartYearlyKind }
    }>
  }>(partner, data, 'electricity/spotPdf')
export const getAllData = async (
  partner: string,
  params: Record<string, string>
): Promise<{ data: string | undefined }> =>
  await getData<Record<string, string>>(partner, params, 'electricity/all')
export const getPartnerId = async (
  code: string
): Promise<string | undefined> => {
  const { status, json } = await fetchUtils.fetchJson(
    `${getPartnerIdUrl}/${code}`,
    {
      method: 'GET'
    }
  )
  return status !== 200 ? undefined : json.partner
}
export const getCognitoLog = async (
  partner: string,
  date: string,
  enhanced: boolean
): Promise<any | undefined> =>
  await getData(partner, { date, enhanced }, 'cognito/log')
export const getCommentLog = async (
  partner: string,
  date: string
): Promise<any | undefined> =>
  await getData(partner, { date }, 'electricity/comment/log')

export const getWeeklyLog = async (
  partner: string,
  date: string
): Promise<any | undefined> =>
  await getData(partner, { date }, 'electricity/weeklyLog')
export const getAnnounces = async (
  partner: string
): Promise<AnnounceItem[] | undefined> => {
  const { status, json } = await fetchJson(partner)(announceUrl, {
    method: 'GET'
  })
  return status !== 200 ? undefined : json
}
export const getElectricityDemand = async (
  partner: string | undefined,
  spin: string,
  date: string
): Promise<ElectricityDemandData | undefined> => {
  const { status, json } = await fetchJson(partner)(
    `${url as string}/electricity/demand/${spin}/${date}`,
    {
      method: 'GET'
    }
  )
  return status !== 200 ? undefined : json
}
export const getElectricityDemandMonthly = async (
  partner: string | undefined,
  spin: string | null | undefined,
  date: string
): Promise<ElectricityDemandData | undefined> => {
  const { status, json } = await fetchJson(partner)(
    `${url as string}/electricity/demand/month/${spin ?? 'all'}/${date}`,
    {
      method: 'GET'
    }
  )
  return status !== 200 ? undefined : json
}
export const getElectricityDemandDaily = async (
  partner: string | undefined,
  spin: string | null | undefined,
  date: string
): Promise<ElectricityDemandData | undefined> => {
  const { status, json } = await fetchJson(partner)(
    `${url as string}/electricity/demand/daily/${spin ?? 'all'}/${date}`,
    {
      method: 'GET'
    }
  )
  return status !== 200 ? undefined : json
}
export const postDemandAlertSettings = async (
  partner: string,
  threshold: number,
  targets: string[]
): Promise<void> => {
  if (isNil(url)) return undefined
  const customHeaders = getCustomHeaders({}, partner)
  if (isNil(customHeaders)) return undefined
  const headers = {}
  customHeaders.forEach((value: string, key: string, _parent: Headers) => {
    assign(headers, { [key]: value })
  })
  await axios
    .post(
      `${url}/electricity/demand/alertSettings`,
      { threshold, targets },
      {
        headers
      }
    )
    .catch((error) => {
      log.error('[functions.postDemandAlertSettings]', {
        threshold,
        targets,
        error
      })
      return { data: undefined, status: 500 }
    })
}
export const getDemandAlartSettings = async (
  partner: string
): Promise<{ threshold: number; targets: string[] } | undefined> => {
  const { status, json } = await fetchJson(partner)(
    `${url as string}/electricity/demand/alertSettings`,
    {
      method: 'GET'
    }
  )
  return status !== 200 ? undefined : json
}

export const postComment = async (
  partner: string,
  targetDate: string,
  name: string,
  email: string,
  target: RaRecord,
  action: string,
  comment: string,
  up: boolean
): Promise<void> => {
  if (isNil(url)) return undefined
  const customHeaders = getCustomHeaders({}, partner)
  if (isNil(customHeaders)) return undefined
  const headers = {}
  customHeaders.forEach((value: string, key: string, _parent: Headers) => {
    assign(headers, { [key]: value })
  })
  await axios
    .post(
      `${url}/electricity/comment`,
      { targetDate, name, email, target, action, comment, up },
      {
        headers
      }
    )
    .catch((error) => {
      log.error('[functions.postComment]', {
        targetDate,
        name,
        email,
        error
      })
      return { data: undefined, status: 500 }
    })
}
export const getComment = async (
  partner: string,
  date: string
): Promise<Comment[] | undefined> => {
  if (isNil(url)) return undefined
  const customHeaders = getCustomHeaders({}, partner)
  if (isNil(customHeaders)) return undefined
  const headers = {}
  customHeaders.forEach((value: string, key: string, _parent: Headers) => {
    assign(headers, { [key]: value })
  })
  const { data } = await axios
    .get(`${url}/electricity/comment/${date}`, {
      headers
    })
    .catch((error) => {
      log.error('[functions.getComment]', {
        date,
        error
      })
      return { data: undefined, status: 500 }
    })
  return data
}
export const doAnalyzeDaily = async (
  partner: string,
  date: string,
  analyze: {
    compare: Array<number | undefined>
    target: Array<number | undefined>
  }
): Promise<string | undefined> => {
  if (isNil(url)) return undefined
  const customHeaders = getCustomHeaders({}, partner)
  if (isNil(customHeaders)) return undefined
  const headers = {}
  customHeaders.forEach((value: string, key: string, _parent: Headers) => {
    assign(headers, { [key]: value })
  })
  const { data } = await axios
    .post(
      `${url}/electricity/analyze/daily/${date}`,
      { ...analyze },
      {
        headers
      }
    )
    .catch((error) => {
      log.error('[functions.doAnalyzeDaily]', {
        date,
        analyze,
        error
      })
      return { data: undefined, status: 500 }
    })
  return data
}
export const doAnalyzeYearly = async (
  partner: string,
  date: string,
  analyze: {
    compare: Array<number | undefined>
  }
): Promise<string | undefined> => {
  if (isNil(url)) return undefined
  const customHeaders = getCustomHeaders({}, partner)
  if (isNil(customHeaders)) return undefined
  const headers = {}
  customHeaders.forEach((value: string, key: string, _parent: Headers) => {
    assign(headers, { [key]: value })
  })
  const { data } = await axios
    .post(
      `${url}/electricity/analyze/yearly/${date}`,
      { ...analyze },
      {
        headers
      }
    )
    .catch((error) => {
      log.error('[functions.doAnalyzeYearly]', {
        date,
        analyze,
        error
      })
      return { data: undefined, status: 500 }
    })
  return data
}
